/**
 * FacilityLink — Vendor Network v1.0
 *
 * Main page with two tabs:
 *  1. My Vendors — org's enrolled vendor network
 *  2. Vendor Directory — global searchable directory
 *
 * Plus a drawer for adding/creating vendors.
 */

import { useCallback, useEffect, useState } from "react";
import {
	Tabs,
	Input,
	Select,
	Button,
	Table,
	Tag,
	Space,
	Drawer,
	InputNumber,
	message,
	Popconfirm,
	Badge,
	Empty,
	Spin,
	Tooltip,
	Switch,
	Modal,
	Upload,
} from "antd";
import {
	PlusOutlined,
	SearchOutlined,
	StarOutlined,
	StarFilled,
	DeleteOutlined,
	EditOutlined,
	CheckCircleOutlined,
	GlobalOutlined,
	TeamOutlined,
	PhoneOutlined,
	MailOutlined,
	LinkOutlined,
	UploadOutlined,
	FileTextOutlined,
	DownloadOutlined,
	WarningOutlined,
	InboxOutlined,
} from "@ant-design/icons";
import { useFacilityLinkStore } from "@/stores/facility-link-store";
import {
	listTrades,
	searchGlobalVendors,
	listMyVendors,
	enrollVendor,
	createAndEnrollVendor,
	updateMyVendor,
	removeMyVendor,
	importVendors,
} from "@/services/facility-link-service";
import type {
	Trade,
	Vendor,
	OrgVendorWithDetails,
	ImportVendorRow,
} from "@/services/facility-link-service";
import "./FacilityLink.scss";

const { TabPane } = Tabs;

const FacilityLink = () => {
	const store = useFacilityLinkStore();

	// Drawer state
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [drawerMode, setDrawerMode] = useState<"create" | "edit">("create");
	const [editingOrgVendor, setEditingOrgVendor] = useState<OrgVendorWithDetails | null>(null);
	const [submitting, setSubmitting] = useState(false);

	// Create form state
	const [form, setForm] = useState({
		companyName: "",
		contactName: "",
		contactEmail: "",
		contactPhone: "",
		website: "",
		description: "",
		tradeIds: [] as number[],
		nickname: "",
		hourlyRate: undefined as number | undefined,
		isPreferred: false,
		notes: "",
	});

	// Import modal state
	const [importModalOpen, setImportModalOpen] = useState(false);
	const [importTab, setImportTab] = useState<"upload" | "paste">("upload");
	const [importPasteText, setImportPasteText] = useState("");
	const [importParsedRows, setImportParsedRows] = useState<(ImportVendorRow & { _valid: boolean; _error?: string })[]>([]);
	const [importPhase, setImportPhase] = useState<"input" | "preview">("input");
	const [importAutoEnroll, setImportAutoEnroll] = useState(true);
	const [importing, setImporting] = useState(false);
	// ─── CSV Parsing ─────────────────────────────────────────────────────────

	const CSV_HEADERS = ["Company Name", "Contact Name", "Email", "Phone", "Website", "Description", "Trades"];

	const parseCSVLine = (line: string): string[] => {
		const result: string[] = [];
		let current = "";
		let inQuotes = false;
		for (let i = 0; i < line.length; i++) {
			const ch = line[i];
			if (inQuotes) {
				if (ch === '"' && line[i + 1] === '"') {
					current += '"';
					i++;
				} else if (ch === '"') {
					inQuotes = false;
				} else {
					current += ch;
				}
			} else if (ch === '"') {
				inQuotes = true;
			} else if (ch === "," || ch === "\t") {
				result.push(current.trim());
				current = "";
			} else {
				current += ch;
			}
		}
		result.push(current.trim());
		return result;
	};

	const parseCSVData = (text: string) => {
		const lines = text.split(/\r?\n/).filter((l) => l.trim());
		if (lines.length < 2) {
			message.warning("No data rows found. Make sure you include a header row.");
			return;
		}

		const headerLine = parseCSVLine(lines[0]);
		const nameIdx = headerLine.findIndex((h) => /company\s*name/i.test(h));
		if (nameIdx === -1) {
			message.error("Could not find a 'Company Name' column in the header row");
			return;
		}

		const contactIdx = headerLine.findIndex((h) => /contact\s*name/i.test(h));
		const emailIdx = headerLine.findIndex((h) => /email/i.test(h));
		const phoneIdx = headerLine.findIndex((h) => /phone/i.test(h));
		const websiteIdx = headerLine.findIndex((h) => /website/i.test(h));
		const descIdx = headerLine.findIndex((h) => /description/i.test(h));
		const tradesIdx = headerLine.findIndex((h) => /trades?/i.test(h));

		const rows: (ImportVendorRow & { _valid: boolean; _error?: string })[] = [];

		for (let i = 1; i < lines.length; i++) {
			const cols = parseCSVLine(lines[i]);
			const companyName = cols[nameIdx] ?? "";
			const valid = !!companyName.trim();

			rows.push({
				companyName,
				contactName: contactIdx >= 0 ? cols[contactIdx] || undefined : undefined,
				contactEmail: emailIdx >= 0 ? cols[emailIdx] || undefined : undefined,
				contactPhone: phoneIdx >= 0 ? cols[phoneIdx] || undefined : undefined,
				website: websiteIdx >= 0 ? cols[websiteIdx] || undefined : undefined,
				description: descIdx >= 0 ? cols[descIdx] || undefined : undefined,
				trades: tradesIdx >= 0 && cols[tradesIdx]
					? cols[tradesIdx].split(/[,;]/).map((t) => t.trim()).filter(Boolean)
					: undefined,
				_valid: valid,
				_error: valid ? undefined : "Company name is required",
			});
		}

		setImportParsedRows(rows);
		setImportPhase("preview");
	};

	const handleFileUpload = (file: File) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			const text = e.target?.result as string;
			if (text) parseCSVData(text);
		};
		reader.readAsText(file);
		return false;
	};

	const handlePasteParse = () => {
		if (!importPasteText.trim()) {
			message.warning("Please paste some data first");
			return;
		}
		parseCSVData(importPasteText);
	};

	const downloadTemplate = () => {
		const csv = `${CSV_HEADERS.join(",")}\n"ABC Plumbing","John Doe","john@abc.com","(555) 555-0100","https://abcplumbing.com","Full-service plumbing","Plumbing"\n"Metro HVAC Solutions","Jane Smith","jane@metrohvac.com","(555) 555-0200","","Commercial HVAC services","HVAC, Refrigeration"\n`;
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "vendor-import-template.csv";
		a.click();
		URL.revokeObjectURL(url);
	};

	const resetImportModal = () => {
		setImportPhase("input");
		setImportTab("upload");
		setImportPasteText("");
		setImportParsedRows([]);
		setImportAutoEnroll(true);
	};

	const handleImport = async () => {
		const validRows = importParsedRows.filter((r) => r._valid);
		if (!validRows.length) {
			message.warning("No valid vendors to import");
			return;
		}
		setImporting(true);
		try {
			const payload: ImportVendorRow[] = validRows.map(({ _valid, _error, ...rest }) => rest);
			const result = await importVendors(payload, importAutoEnroll);
			const parts: string[] = [`${result.imported} vendor${result.imported !== 1 ? "s" : ""} imported`];
			if (result.enrolled) parts.push(`${result.enrolled} enrolled`);
			if (result.errors.length) parts.push(`${result.errors.length} error${result.errors.length !== 1 ? "s" : ""}`);
			message.success(parts.join(", "));
			setImportModalOpen(false);
			resetImportModal();
			loadMyVendors();
			if (store.activeTab === "directory") loadGlobalVendors();
		} catch (err: any) {
			message.error(err.message || "Import failed");
		} finally {
			setImporting(false);
		}
	};

	// ─── Data Loading ────────────────────────────────────────────────────────

	const loadTrades = useCallback(async () => {
		if (store.tradesLoaded) return;
		try {
			const trades = await listTrades();
			store.setTrades(trades);
		} catch {
			message.error("Failed to load trades");
		}
	}, [store.tradesLoaded]);

	const loadMyVendors = useCallback(async () => {
		store.setMyLoading(true);
		try {
			const vendors = await listMyVendors(store.myTradeFilter, store.mySearch || undefined);
			store.setMyVendors(vendors);
		} catch {
			message.error("Failed to load vendors");
		} finally {
			store.setMyLoading(false);
		}
	}, [store.myTradeFilter, store.mySearch]);

	const loadGlobalVendors = useCallback(async () => {
		store.setGlobalLoading(true);
		try {
			const vendors = await searchGlobalVendors(
				store.globalSearch || undefined,
				store.globalTradeFilter,
			);
			store.setGlobalVendors(vendors);
		} catch {
			message.error("Failed to search vendors");
		} finally {
			store.setGlobalLoading(false);
		}
	}, [store.globalSearch, store.globalTradeFilter]);

	useEffect(() => {
		loadTrades();
	}, [loadTrades]);

	useEffect(() => {
		loadMyVendors();
	}, [loadMyVendors]);

	useEffect(() => {
		if (store.activeTab === "directory") {
			loadGlobalVendors();
		}
	}, [store.activeTab, loadGlobalVendors]);

	// ─── Handlers ────────────────────────────────────────────────────────────

	const openCreateDrawer = () => {
		setDrawerMode("create");
		setEditingOrgVendor(null);
		setForm({
			companyName: "",
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			website: "",
			description: "",
			tradeIds: [],
			nickname: "",
			hourlyRate: undefined,
			isPreferred: false,
			notes: "",
		});
		setDrawerOpen(true);
	};

	const openEditDrawer = (v: OrgVendorWithDetails) => {
		setDrawerMode("edit");
		setEditingOrgVendor(v);
		setForm({
			companyName: v.companyName,
			contactName: v.contactName ?? "",
			contactEmail: v.contactEmail ?? "",
			contactPhone: v.contactPhone ?? "",
			website: v.website ?? "",
			description: v.description ?? "",
			tradeIds: v.trades.map((t) => t.id),
			nickname: v.nickname ?? "",
			hourlyRate: v.hourlyRate ?? undefined,
			isPreferred: v.isPreferred,
			notes: v.notes ?? "",
		});
		setDrawerOpen(true);
	};

	const handleSave = async () => {
		if (drawerMode === "create") {
			if (!form.companyName.trim()) {
				message.warning("Company name is required");
				return;
			}
			setSubmitting(true);
			try {
				await createAndEnrollVendor({
					companyName: form.companyName,
					contactName: form.contactName || undefined,
					contactEmail: form.contactEmail || undefined,
					contactPhone: form.contactPhone || undefined,
					website: form.website || undefined,
					description: form.description || undefined,
					tradeIds: form.tradeIds.length ? form.tradeIds : undefined,
					nickname: form.nickname || undefined,
					hourlyRate: form.hourlyRate,
					isPreferred: form.isPreferred,
					notes: form.notes || undefined,
				});
				message.success("Vendor created and added to your network");
				setDrawerOpen(false);
				loadMyVendors();
			} catch {
				message.error("Failed to create vendor");
			} finally {
				setSubmitting(false);
			}
		} else if (drawerMode === "edit" && editingOrgVendor) {
			setSubmitting(true);
			try {
				await updateMyVendor(editingOrgVendor.id, {
					nickname: form.nickname || undefined,
					hourlyRate: form.hourlyRate,
					isPreferred: form.isPreferred,
					notes: form.notes || undefined,
				});
				message.success("Vendor settings updated");
				setDrawerOpen(false);
				loadMyVendors();
			} catch {
				message.error("Failed to update vendor");
			} finally {
				setSubmitting(false);
			}
		}
	};

	const handleEnrollFromDirectory = async (vendor: Vendor) => {
		try {
			await enrollVendor({ vendorId: vendor.id });
			message.success(`${vendor.companyName} added to your network`);
			loadMyVendors();
		} catch {
			message.error("Failed to add vendor");
		}
	};

	const handleRemove = async (orgVendorId: number) => {
		try {
			await removeMyVendor(orgVendorId);
			message.success("Vendor removed from your network");
			store.removeFromMyVendors(orgVendorId);
		} catch {
			message.error("Failed to remove vendor");
		}
	};

	const handleTogglePreferred = async (v: OrgVendorWithDetails) => {
		try {
			await updateMyVendor(v.id, { isPreferred: !v.isPreferred });
			store.updateInMyVendors(v.id, { isPreferred: !v.isPreferred });
		} catch {
			message.error("Failed to update");
		}
	};

	// ─── Trade tag colors ────────────────────────────────────────────────────

	const tradeColors = [
		"blue", "cyan", "green", "purple", "magenta",
		"orange", "gold", "red", "geekblue", "lime",
		"volcano", "processing", "warning", "error", "success",
	];

	const getTradeColor = (tradeId: number) => tradeColors[tradeId % tradeColors.length];

	// Check if a global vendor is already enrolled
	const enrolledVendorIds = new Set(store.myVendors.map((v) => v.vendorId));

	// ─── My Vendors columns ─────────────────────────────────────────────────

	const myVendorColumns = [
		{
			title: "Vendor",
			key: "vendor",
			render: (_: unknown, record: OrgVendorWithDetails) => (
				<div className="fs-vendor-cell">
					<div className="fs-vendor-name">
						{record.nickname || record.companyName}
						{record.isVerified && (
							<Tooltip title="Verified Vendor">
								<CheckCircleOutlined style={{ color: "#52c41a", marginLeft: 6, fontSize: 13 }} />
							</Tooltip>
						)}
						{record.isPreferred && (
							<Tooltip title="Preferred Vendor">
								<StarFilled style={{ color: "#faad14", marginLeft: 6, fontSize: 13 }} />
							</Tooltip>
						)}
					</div>
					{record.nickname && record.nickname !== record.companyName && (
						<div className="fs-vendor-company">{record.companyName}</div>
					)}
				</div>
			),
		},
		{
			title: "Trades",
			key: "trades",
			render: (_: unknown, record: OrgVendorWithDetails) => (
				<div className="fs-trades-cell">
					{record.trades.map((t) => (
						<Tag key={t.id} color={getTradeColor(t.id)}>{t.name}</Tag>
					))}
					{!record.trades.length && <span style={{ color: "#999" }}>—</span>}
				</div>
			),
		},
		{
			title: "Contact",
			key: "contact",
			render: (_: unknown, record: OrgVendorWithDetails) => (
				<div className="fs-contact-cell">
					{record.contactName && <div>{record.contactName}</div>}
					{record.contactEmail && (
						<div className="fs-contact-detail">
							<MailOutlined /> {record.contactEmail}
						</div>
					)}
					{record.contactPhone && (
						<div className="fs-contact-detail">
							<PhoneOutlined /> {record.contactPhone}
						</div>
					)}
				</div>
			),
			responsive: ["md" as const],
		},
		{
			title: "Rate",
			key: "hourlyRate",
			width: 100,
			render: (_: unknown, record: OrgVendorWithDetails) =>
				record.hourlyRate ? `$${record.hourlyRate}/hr` : "—",
		},
		{
			title: "Actions",
			key: "actions",
			width: 140,
			render: (_: unknown, record: OrgVendorWithDetails) => (
				<Space>
					<Tooltip title={record.isPreferred ? "Remove preferred" : "Set preferred"}>
						<Button
							type="text"
							size="small"
							icon={record.isPreferred ? <StarFilled style={{ color: "#faad14" }} /> : <StarOutlined />}
							onClick={() => handleTogglePreferred(record)}
						/>
					</Tooltip>
					<Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditDrawer(record)} />
					<Popconfirm title="Remove from your network?" onConfirm={() => handleRemove(record.id)}>
						<Button type="text" danger size="small" icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	// ─── Directory columns ──────────────────────────────────────────────────

	const directoryColumns = [
		{
			title: "Vendor",
			key: "vendor",
			render: (_: unknown, record: Vendor) => (
				<div className="fs-vendor-cell">
					<div className="fs-vendor-name">
						{record.companyName}
						{record.isVerified && (
							<Tooltip title="Verified">
								<CheckCircleOutlined style={{ color: "#52c41a", marginLeft: 6, fontSize: 13 }} />
							</Tooltip>
						)}
					</div>
					{record.description && (
						<div className="fs-vendor-desc">{record.description}</div>
					)}
				</div>
			),
		},
		{
			title: "Trades",
			key: "trades",
			render: (_: unknown, record: Vendor) => (
				<div className="fs-trades-cell">
					{record.trades.map((t) => (
						<Tag key={t.id} color={getTradeColor(t.id)}>{t.name}</Tag>
					))}
					{!record.trades.length && <span style={{ color: "#999" }}>No trades listed</span>}
				</div>
			),
		},
		{
			title: "Contact",
			key: "contact",
			render: (_: unknown, record: Vendor) => (
				<div className="fs-contact-cell">
					{record.contactName && <div>{record.contactName}</div>}
					{record.contactEmail && (
						<div className="fs-contact-detail">
							<MailOutlined /> {record.contactEmail}
						</div>
					)}
					{record.website && (
						<div className="fs-contact-detail">
							<LinkOutlined /> {record.website}
						</div>
					)}
				</div>
			),
			responsive: ["md" as const],
		},
		{
			title: "",
			key: "action",
			width: 160,
			render: (_: unknown, record: Vendor) =>
				enrolledVendorIds.has(record.id) ? (
					<Tag color="green" icon={<CheckCircleOutlined />}>In Your Network</Tag>
				) : (
					<Button
						type="primary"
						size="small"
						icon={<PlusOutlined />}
						onClick={() => handleEnrollFromDirectory(record)}
					>
						Add to Network
					</Button>
				),
		},
	];

	// ─── Render ──────────────────────────────────────────────────────────────

	return (
		<div className="facility-link-page">
			<div className="fs-header">
				<div className="fs-header-left">
					<TeamOutlined className="fs-header-icon" />
					<div>
						<h2>FacilityLink</h2>
						<p>Manage your vendor network and discover service providers</p>
					</div>
				</div>
				<Space>
					<Button icon={<UploadOutlined />} onClick={() => { resetImportModal(); setImportModalOpen(true); }}>
						Import Vendors
					</Button>
					<Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer}>
						Add Vendor
					</Button>
				</Space>
			</div>

			<Tabs
				activeKey={store.activeTab}
				onChange={(key) => store.setActiveTab(key as "my-vendors" | "directory")}
				className="fs-tabs"
			>
				<TabPane
					tab={
						<span>
							<TeamOutlined /> My Vendors
							{store.myVendors.length > 0 && (
								<Badge
									count={store.myVendors.length}
									style={{ marginLeft: 8, backgroundColor: "#6c5ce7" }}
									size="small"
								/>
							)}
						</span>
					}
					key="my-vendors"
				>
					<div className="fs-toolbar">
						<Input
							placeholder="Search vendors..."
							prefix={<SearchOutlined />}
							value={store.mySearch}
							onChange={(e) => store.setMySearch(e.target.value)}
							style={{ width: 260 }}
							allowClear
						/>
						<Select
							placeholder="Filter by trade"
							allowClear
							style={{ width: 200 }}
							value={store.myTradeFilter}
							onChange={(v) => store.setMyTradeFilter(v)}
							options={store.trades.map((t) => ({ label: t.name, value: t.id }))}
						/>
					</div>

					{store.myVendors.length === 0 && !store.myLoading ? (
						<Empty
							description={
								<span>
									No vendors in your network yet.{" "}
									<a onClick={() => store.setActiveTab("directory")}>Browse the directory</a>{" "}
									or <a onClick={openCreateDrawer}>add a new vendor</a>.
								</span>
							}
							style={{ padding: "60px 0" }}
						/>
					) : (
						<Table
							dataSource={store.myVendors}
							columns={myVendorColumns}
							rowKey="id"
							loading={store.myLoading}
							size="middle"
							pagination={{ pageSize: 20, showSizeChanger: false }}
						/>
					)}
				</TabPane>

				<TabPane
					tab={
						<span>
							<GlobalOutlined /> Vendor Directory
						</span>
					}
					key="directory"
				>
					<div className="fs-toolbar">
						<Input
							placeholder="Search directory..."
							prefix={<SearchOutlined />}
							value={store.globalSearch}
							onChange={(e) => store.setGlobalSearch(e.target.value)}
							style={{ width: 260 }}
							allowClear
							onPressEnter={loadGlobalVendors}
						/>
						<Select
							placeholder="Filter by trade"
							allowClear
							style={{ width: 200 }}
							value={store.globalTradeFilter}
							onChange={(v) => store.setGlobalTradeFilter(v)}
							options={store.trades.map((t) => ({ label: t.name, value: t.id }))}
						/>
						<Button onClick={loadGlobalVendors} icon={<SearchOutlined />}>
							Search
						</Button>
					</div>

					{store.globalLoading ? (
						<div className="fs-loading"><Spin size="large" /></div>
					) : store.globalVendors.length === 0 ? (
						<Empty
							description="No vendors found. Try a different search or add a new vendor."
							style={{ padding: "60px 0" }}
						/>
					) : (
						<Table
							dataSource={store.globalVendors}
							columns={directoryColumns}
							rowKey="id"
							size="middle"
							pagination={{ pageSize: 20, showSizeChanger: false }}
						/>
					)}
				</TabPane>
			</Tabs>

			{/* Add / Edit Vendor Modal */}
			<Drawer
				title={null}
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				width={680}
				destroyOnClose
				closable={false}
				styles={{ body: { padding: 0 } }}
				footer={null}
			>
				<div className="fs-modal-vendor">
					<div className="fs-modal-header">
						<div className="fs-modal-header-icon">
							<TeamOutlined />
						</div>
						<div>
							<h3>{drawerMode === "create" ? "Add New Vendor" : "Edit Vendor Settings"}</h3>
							<p>{drawerMode === "create" ? "Create a vendor and add them to your network" : "Update this vendor's network settings"}</p>
						</div>
					</div>

					<div className="fs-modal-body">
						{drawerMode === "create" && (
							<>
								<div className="fs-modal-section">
									<div className="fs-modal-section-label">
										<GlobalOutlined />
										<span>Company</span>
									</div>
									<div className="fs-modal-grid">
										<div className="fs-modal-field fs-modal-field-full">
											<label>Company Name <span className="fs-required">*</span></label>
											<Input
												value={form.companyName}
												onChange={(e) => setForm({ ...form, companyName: e.target.value })}
												placeholder="e.g. ABC Plumbing Services"
												size="large"
											/>
										</div>
										<div className="fs-modal-field">
											<label>Website</label>
											<Input
												value={form.website}
												onChange={(e) => setForm({ ...form, website: e.target.value })}
												placeholder="https://..."
												prefix={<LinkOutlined style={{ color: "#bbb" }} />}
											/>
										</div>
										<div className="fs-modal-field">
											<label>Trades</label>
											<Select
												mode="multiple"
												value={form.tradeIds}
												onChange={(v) => setForm({ ...form, tradeIds: v })}
												options={store.trades.map((t) => ({ label: t.name, value: t.id }))}
												placeholder="Select trades..."
												style={{ width: "100%" }}
											/>
										</div>
										<div className="fs-modal-field fs-modal-field-full">
											<label>Description</label>
											<Input.TextArea
												value={form.description}
												onChange={(e) => setForm({ ...form, description: e.target.value })}
												placeholder="Brief description of services offered"
												rows={2}
											/>
										</div>
									</div>
								</div>

								<div className="fs-modal-section">
									<div className="fs-modal-section-label">
										<PhoneOutlined />
										<span>Contact</span>
									</div>
									<div className="fs-modal-grid">
										<div className="fs-modal-field">
											<label>Contact Name</label>
											<Input
												value={form.contactName}
												onChange={(e) => setForm({ ...form, contactName: e.target.value })}
												placeholder="Primary contact"
											/>
										</div>
										<div className="fs-modal-field">
											<label>Email</label>
											<Input
												value={form.contactEmail}
												onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
												placeholder="vendor@example.com"
												prefix={<MailOutlined style={{ color: "#bbb" }} />}
											/>
										</div>
										<div className="fs-modal-field">
											<label>Phone</label>
											<Input
												value={form.contactPhone}
												onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
												placeholder="(555) 555-0100"
												prefix={<PhoneOutlined style={{ color: "#bbb" }} />}
											/>
										</div>
									</div>
								</div>
							</>
						)}

						<div className="fs-modal-section">
							<div className="fs-modal-section-label">
								<StarOutlined />
								<span>Network Settings</span>
							</div>
							<div className="fs-modal-grid">
								<div className="fs-modal-field">
									<label>Nickname <span className="fs-optional">(internal)</span></label>
									<Input
										value={form.nickname}
										onChange={(e) => setForm({ ...form, nickname: e.target.value })}
										placeholder="Internal name for this vendor"
									/>
								</div>
								<div className="fs-modal-field">
									<label>Hourly Rate</label>
									<InputNumber
										value={form.hourlyRate}
										onChange={(v) => setForm({ ...form, hourlyRate: v ?? undefined })}
										style={{ width: "100%" }}
										min={0}
										prefix="$"
										placeholder="0.00"
									/>
								</div>
								<div className="fs-modal-field fs-modal-field-full">
									<label>Notes</label>
									<Input.TextArea
										value={form.notes}
										onChange={(e) => setForm({ ...form, notes: e.target.value })}
										placeholder="Internal notes about this vendor..."
										rows={2}
									/>
								</div>
								<div className="fs-modal-field fs-modal-field-full fs-modal-switch-row">
									<div className="fs-modal-switch-content">
										<StarFilled style={{ color: form.isPreferred ? "#faad14" : "#d9d9d9", fontSize: 16 }} />
										<div>
											<div className="fs-modal-switch-label">Preferred Vendor</div>
											<div className="fs-modal-switch-desc">Preferred vendors appear first in assignment lists</div>
										</div>
									</div>
									<Switch
										checked={form.isPreferred}
										onChange={(v) => setForm({ ...form, isPreferred: v })}
									/>
								</div>
							</div>
						</div>
					</div>

					<div className="fs-modal-footer">
						<Button size="large" onClick={() => setDrawerOpen(false)}>
							Cancel
						</Button>
						<Button type="primary" size="large" loading={submitting} onClick={handleSave}>
							{drawerMode === "create" ? "Create & Add to Network" : "Save Changes"}
						</Button>
					</div>
				</div>
			</Drawer>

			{/* Import Vendors Modal */}
			<Modal
				open={importModalOpen}
				onCancel={() => { setImportModalOpen(false); resetImportModal(); }}
				title={null}
				footer={null}
				width={760}
				destroyOnClose
				className="fs-import-modal"
			>
				<div className="fs-import-container">
					<div className="fs-import-header">
						<div className="fs-import-header-icon">
							<UploadOutlined />
						</div>
						<div>
							<h3>Import Vendors</h3>
							<p>Bulk import vendors from a CSV file or spreadsheet</p>
						</div>
					</div>

					{importPhase === "input" ? (
						<div className="fs-import-body">
							<div className="fs-import-tabs">
								<button
									className={`fs-import-tab ${importTab === "upload" ? "active" : ""}`}
									onClick={() => setImportTab("upload")}
								>
									<UploadOutlined /> Upload CSV
								</button>
								<button
									className={`fs-import-tab ${importTab === "paste" ? "active" : ""}`}
									onClick={() => setImportTab("paste")}
								>
									<FileTextOutlined /> Paste Data
								</button>
							</div>

							{importTab === "upload" ? (
								<div className="fs-import-upload-area">
									<Upload.Dragger
										accept=".csv,.tsv,.txt"
										beforeUpload={handleFileUpload}
										showUploadList={false}
										multiple={false}
									>
										<p className="ant-upload-drag-icon">
											<InboxOutlined />
										</p>
										<p className="ant-upload-text">Click or drag a CSV file here</p>
										<p className="ant-upload-hint">
											Supports .csv and .tsv files with a header row
										</p>
									</Upload.Dragger>
								</div>
							) : (
								<div className="fs-import-paste-area">
									<Input.TextArea
										value={importPasteText}
										onChange={(e) => setImportPasteText(e.target.value)}
										placeholder={`Paste your data here. Include a header row:\n\nCompany Name,Contact Name,Email,Phone,Website,Description,Trades\nABC Plumbing,John Doe,john@abc.com,(555) 555-0100,https://abc.com,Full-service plumber,Plumbing`}
										rows={10}
										style={{ fontFamily: "monospace", fontSize: 12 }}
									/>
									<Button
										type="primary"
										onClick={handlePasteParse}
										style={{ marginTop: 12 }}
										disabled={!importPasteText.trim()}
									>
										Parse Data
									</Button>
								</div>
							)}

							<div className="fs-import-template">
								<Button type="link" icon={<DownloadOutlined />} onClick={downloadTemplate}>
									Download CSV template
								</Button>
								<span className="fs-import-template-hint">
									Required: Company Name. Optional: Contact Name, Email, Phone, Website, Description, Trades
								</span>
							</div>
						</div>
					) : (
						<div className="fs-import-body">
							<div className="fs-import-summary">
								<div className="fs-import-stat fs-import-stat-ok">
									<CheckCircleOutlined />
									<span>{importParsedRows.filter((r) => r._valid).length} vendors ready</span>
								</div>
								{importParsedRows.some((r) => !r._valid) && (
									<div className="fs-import-stat fs-import-stat-error">
										<WarningOutlined />
										<span>{importParsedRows.filter((r) => !r._valid).length} error{importParsedRows.filter((r) => !r._valid).length !== 1 ? "s" : ""}</span>
									</div>
								)}
							</div>

							<div className="fs-import-preview-table">
								<Table
									dataSource={importParsedRows}
									rowKey={(_, i) => String(i)}
									size="small"
									pagination={importParsedRows.length > 50 ? { pageSize: 50 } : false}
									scroll={{ x: 700 }}
									rowClassName={(record) => record._valid ? "" : "fs-import-row-error"}
									columns={[
										{
											title: "",
											width: 36,
											render: (_: unknown, record: any) =>
												record._valid
													? <CheckCircleOutlined style={{ color: "#52c41a" }} />
													: <Tooltip title={record._error}><WarningOutlined style={{ color: "#ff4d4f" }} /></Tooltip>,
										},
										{ title: "Company Name", dataIndex: "companyName", ellipsis: true },
										{ title: "Contact", dataIndex: "contactName", ellipsis: true, render: (v: string) => v || "—" },
										{ title: "Email", dataIndex: "contactEmail", ellipsis: true, render: (v: string) => v || "—" },
										{ title: "Phone", dataIndex: "contactPhone", width: 130, render: (v: string) => v || "—" },
										{
											title: "Trades",
											dataIndex: "trades",
											render: (trades: string[] | undefined) =>
												trades?.length ? trades.join(", ") : "—",
										},
									]}
								/>
							</div>

							<div className="fs-import-options">
								<div className="fs-import-enroll-toggle">
									<Switch checked={importAutoEnroll} onChange={setImportAutoEnroll} />
									<div>
										<div className="fs-import-enroll-label">Auto-enroll into My Network</div>
										<div className="fs-import-enroll-desc">Imported vendors will be added to your vendor network automatically</div>
									</div>
								</div>
							</div>

							<div className="fs-import-actions">
								<Button onClick={() => { setImportPhase("input"); setImportParsedRows([]); }}>
									Back
								</Button>
								<Button
									type="primary"
									size="large"
									loading={importing}
									onClick={handleImport}
									disabled={!importParsedRows.some((r) => r._valid)}
									icon={<UploadOutlined />}
								>
									Import {importParsedRows.filter((r) => r._valid).length} Vendor{importParsedRows.filter((r) => r._valid).length !== 1 ? "s" : ""}
								</Button>
							</div>
						</div>
					)}
				</div>
			</Modal>
		</div>
	);
};

export { FacilityLink };
